import React, { useEffect, useState , useCallback} from "react";
import { toast } from "react-toastify";
import { getApiUrl } from "../utils/api";
import DynamicSidebar from "../components/DynamicSidebar";
import ProductCard from "../components/ProductCard";
import ComboSideMenu from "../components/ComboSideMenu";
import BulkSubcategoryModal from "../components/BulkSubcategoryModal";
import { useLocation, useNavigate } from "react-router-dom";

const Mainproducts = () => {
  const [products, setProducts] = useState([]);
  const [selectedSubcategory, setSelectedSubcategory] = useState(null);

  const [bulkCombos, setBulkCombos] = useState([]);
  const [selectedBulkCombo, setSelectedBulkCombo] = useState(null);
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [activeBulkSubcategory, setActiveBulkSubcategory] = useState(null);

  const [comboItems, setComboItems] = useState([]);
  const [sideOpen, setSideOpen] = useState(false);

  const [selectedSize, setSelectedSize] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasProducts, setHasProducts] = useState(true); // To handle "No products"

  const LIMIT = 12;

  const sizeOptions = ["M", "L", "XL", "XXL", "3XL"];

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const subId = location.state?.subcategoryId;
    if (subId) {
      setSelectedSubcategory(subId);
      setPage(1);
      fetchProducts(subId, 1);
    }
  }, []);

  const comboType = location.state?.comboType || null;
  const isBulk = comboType === "BULK";

  useEffect(() => {
    if (isBulk) fetchBulkCombos();
  }, [isBulk]);

  const fetchBulkCombos = async () => {
    try {
      const res = await fetch(getApiUrl("bulk-list"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (data.success) {
        setBulkCombos(data.data);
        setShowBulkModal(true);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveItem = (id) => {
    setComboItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleBulkSelect = (combo) => {
    setSelectedBulkCombo(combo);
    setActiveBulkSubcategory(combo.subcategoryId);
    setShowBulkModal(false);
    setPage(1);
    fetchProducts(combo.subcategoryId, 1);
  };
  const fetchProducts = useCallback(
    async (subcategoryId, pageNo = 1, sizeFilter = "") => {
      setSelectedSubcategory(subcategoryId);

      try {
        const res = await fetch(getApiUrl("products/by-subcategory"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            subcategoryid: subcategoryId,
            page: pageNo,
            limit: LIMIT,
            size: sizeFilter || undefined, // only send size if selected
          }),
        });

        const data = await res.json();

        if (data.success) {
          const normalized = data.data.map((p) => ({ ...p, id: p._id }));

          setProducts(normalized);
          setPage(data.page);
          setTotalPages(data.totalPages);

          setHasProducts(normalized.length > 0);
        } else {
          setProducts([]);
          setHasProducts(false);
          setTotalPages(1);
          setPage(1);
        }
      } catch (err) {
        toast.error("Failed to load products");
        console.error(err);
        setProducts([]);
        setHasProducts(false);
      }
    },
    [],
  );
  const handleSizeSelect = (size) => {
    const newSize = size === selectedSize ? "" : size;
    setSelectedSize(newSize);
    setPage(1);

    if (selectedSubcategory) {
      fetchProducts(selectedSubcategory, 1, newSize); // reset page to 1
    }
  };

  const handleComboSelect = (item) => {
    setComboItems((prev) => {
      if (isBulk) return [...prev, item];
      if (prev.some((p) => p.id === item.id)) {
        toast.warning("Product already selected");
        return prev;
      }
      if (
        comboType !== "MIXED" &&
        prev.some((p) => p.subcategoryId === item.subcategoryId)
      ) {
        toast.warning("One product per category only");
        return prev;
      }
      return [...prev, item];
    });

    setSideOpen(true);
  };

  const handleAddToCart = async (product) => {
    if (!selectedSize) return toast.warning("Select size");

    const userId = localStorage.getItem("userId");

    const res = await fetch(getApiUrl("cart-add"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        userId,
        productId: product.id,
        selectedSize,
        quantity: 1,
      }),
    });

    const data = await res.json();
    if (data.success) navigate("/bskian-cart");
  };

  const handleAddWishlist = async (product) => {
    try {
      let userId = localStorage.getItem("userId");
      if (!userId) {
        userId = "guest_" + Date.now();
        localStorage.setItem("userId", userId);
      }

      const res = await fetch(getApiUrl("wishlist-add"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          productId: product.id,
        }),
      });

      const data = await res.json();

      if (data.success && data.message === "Already in wishlist") {
        toast.info("Already in wishlist 💙");
      } else if (data.success) {
        toast.success("Added to Wishlist ❤️");
      } else {
        toast.error(data.message || "Failed");
      }
    } catch {
      toast.error("Something went wrong ❗");
    }
  };

  const handleCheckout = (orderData) => {
    navigate("/bsk-checkout", {
      state: {
        products: orderData.items,
        totalPrice: orderData.totalPrice,
        discount: orderData.totalDiscount,
        finalPrice: orderData.finalPrice,
        comboType: orderData.comboType,
      },
    });
  };

  /* SIZE FILTER LOGIC */
  const filteredProducts = selectedSize
    ? products.filter((p) => (p.sizes?.[selectedSize] || 0) > 0)
    : products;

  const handleBulkClose = () => {
    setShowBulkModal(false);
    setSelectedBulkCombo(null);
    setActiveBulkSubcategory(null);
    navigate("/bsk-main-product", { replace: true });
  };

  return (
    <div className="container-fluid">
      <BulkSubcategoryModal
        show={showBulkModal}
        combos={bulkCombos}
        onSelect={handleBulkSelect}
        onClose={handleBulkClose}
      />

      <div className="row">
        <div className="col-md-3 col-lg-2 p-0">
          <DynamicSidebar
            onSubCategorySelect={(id) => {
              setPage(1);
              fetchProducts(id, 1);
            }}
            disabledSubcategory={isBulk ? activeBulkSubcategory : null}
            initialSubcategory={location.state?.subcategoryId}
          />
        </div>

        <div className="col-md-9 col-lg-10 p-4">
          {(comboType === "MIXED" || comboType === "BULK") && (
            <div className="alert alert-warning d-flex justify-content-between align-items-center mb-3">
              <strong>
                {comboType === "MIXED"
                  ? "Choose your favorites product from every category — only one per category!"
                  : "Special combo Offer for this category"}
              </strong>

              <button
                className="btn btn-sm btn-outline-danger"
                onClick={() => {
                  setSelectedBulkCombo(null);
                  setActiveBulkSubcategory(null);
                  setComboItems([]);
                  navigate("/bsk-main-product", { replace: true, state: {} });
                }}
              >
                Exit Mode
              </button>
            </div>
          )}

          {products.length > 0 && (
            <div className="size-filter-card mb-4">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="fw-bold">Filter by Size</span>

                {selectedSize && (
                  <button
                    className="btn btn-sm btn-outline-secondary"
                    onClick={() => setSelectedSize("")}
                  >
                    Clear
                  </button>
                )}
              </div>

              <div className="d-flex gap-2 flex-wrap">
                {sizeOptions.map((sz) => (
                  <button
                    key={sz}
                    className={`size-pill ${selectedSize === sz ? "active" : ""}`}
                    onClick={() => handleSizeSelect(sz)}
                  >
                    {sz}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="row">
            {filteredProducts.map((product) => (
              <div key={product.id} className="col-6 col-lg-3 mb-4">
                <ProductCard
                  product={product}
                  comboType={comboType}
                  onComboSelect={handleComboSelect}
                  onAddCart={() => handleAddToCart(product)}
                  onAddWishlist={() => handleAddWishlist(product)}
                />
              </div>
            ))}
          </div>
          {hasProducts ? (
            <div className="d-flex justify-content-between align-items-center my-4">
              <button
                className="btn btn-outline-dark"
                disabled={page <= 1}
                onClick={() =>
                  fetchProducts(selectedSubcategory, page - 1, selectedSize)
                }
              >
                &laquo; Previous
              </button>

              <span>
                Page {page} of {totalPages}
              </span>

              <button
                className="btn btn-outline-dark"
                disabled={page >= totalPages}
                onClick={() =>
                  fetchProducts(selectedSubcategory, page + 1, selectedSize)
                }
              >
                Next &raquo;
              </button>
            </div>
          ) : (
            <div className="text-center my-4">
              <span className="fw-bold text-muted">No products found.</span>
            </div>
          )}

          {(comboType === "MIXED" || comboType === "BULK") && (
            <ComboSideMenu
              open={sideOpen}
              onClose={() => setSideOpen(false)}
              items={comboItems}
              onRemove={handleRemoveItem}
              comboType={comboType}
              onCheckout={handleCheckout}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Mainproducts;
